import { makeAutoObservable } from 'mobx'
import { AxiosError } from 'axios'
import { SURVEY_NAME_LIMIT } from 'shared/constants/limits'
import { toastStore } from 'shared/ui'
import { BlockerStore } from 'shared/ui/Blocker'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  type IParamsCreateSurvey,
  type IResponseFullSurvey,
  SurveysStatus,
} from 'entities/Surveys/api/types'
import { SurveysApi } from 'entities/Surveys'
import { SurveysPath } from 'entities/ExportRouters'
import { NameInputStore } from 'features/CreateOrEdit/NameInput'
import { openDisableSurveyModal } from 'pages/surveys/ui/actions/disableSurvey/openDisableSurveyModal'
import { SurveyEnrollContactsStore } from 'pages/surveys/ui/SurveyView/ui/SurveyEnrollContacts/store/surveyEnrollContactsStore'
import { NpsQuestionStore } from 'pages/surveys/ui/SurveyView/ui/NpsQuestion/store/npsQuestionStore'
import { FallbackActionsStore } from 'pages/surveys/ui/SurveyView/ui/FallbackActions/store/fallbackActionsStore'
import { type IPreviewBubble } from 'pages/surveys/ui/SurveyView/ui/Preview/Preview'

export class SurveyCreateStore {
  id: number | null = null
  isActive = false
  status: SurveysStatus | null = null
  nameInputStore: NameInputStore
  enrollContactsStore: SurveyEnrollContactsStore
  npsQuestionStore: NpsQuestionStore
  fallbackActionsStore: FallbackActionsStore
  errorsMap = new Map<string, string>()
  isSaving = false
  blockerStore: BlockerStore
  isDuplicate = false
  private _blockerConfig: {
    title: string
    primaryText: string
    primaryAction: () => Promise<unknown>
    additionalSecondaryActionText?: string
    modalType: ModalTypeList
  } = {
    title: '',
    primaryText: '',
    primaryAction: async () => this.onSaveSurvey({ withError: true }),
    modalType: ModalTypeList.WARNING,
  }

  private _syncBlockerConfig = () => {
    if (this.isActive) {
      this._blockerConfig.title = 'Publish survey before closing?'
      this._blockerConfig.primaryText = 'Publish changes'
      this._blockerConfig.primaryAction = async () =>
        this.onActivateSurvey({ withError: true, skipNavigation: true })
      this._blockerConfig.additionalSecondaryActionText = "Don't publish"
    } else {
      this._blockerConfig.title = 'Save survey before closing?'
      this._blockerConfig.primaryText =
        this.status === SurveysStatus.Draft || this.status === null
          ? 'Save as draft'
          : 'Save changes'
      this._blockerConfig.primaryAction = async () => this.onSaveSurvey({ withError: true })
      this._blockerConfig.additionalSecondaryActionText = "Don't save"
    }
  }

  constructor(
    readonly entities: IEntitiesStore,
    private _surveyId: number | undefined,
    isDuplicate?: boolean
  ) {
    this.nameInputStore = new NameInputStore({
      focusOnMount: !_surveyId || isDuplicate,
      isResetErrorOnChange: true,
      limit: SURVEY_NAME_LIMIT,
    })
    this.enrollContactsStore = new SurveyEnrollContactsStore(entities)
    this.npsQuestionStore = new NpsQuestionStore(entities.organizationStore.name)
    this.fallbackActionsStore = new FallbackActionsStore()
    if (_surveyId && !isDuplicate) this.id = _surveyId
    this.isDuplicate = isDuplicate ?? false

    makeAutoObservable(this)
    this._syncBlockerConfig()
    this.blockerStore = new BlockerStore(this, this._blockerConfig)
  }

  get isEditMode() {
    return Boolean(this.id) && !this.isDuplicate
  }

  init = async (response: IResponseFullSurvey) => {
    if (this.isDuplicate) {
      this.isActive = false
      this.status = null
    } else {
      this.isActive = response.is_active
      this.status = response.status
    }

    this.nameInputStore.init(this.isDuplicate ? `Copy of ${response.name}` : response.name)
    this.npsQuestionStore.setCompanyName(response.question?.company_name ?? '')
    this.fallbackActionsStore.syncOrigin(response.settings)
    try {
      await this.enrollContactsStore.init(response)
    } catch (e) {
      logger.error(e)
    } finally {
      this._syncBlockerConfig()
      this.blockerStore.updateCache()
    }
  }

  get previewBubbles(): IPreviewBubble[] {
    return [...this.npsQuestionStore.previewBubble]
  }

  get params(): IParamsCreateSurvey {
    return {
      name: this.nameInputStore.name,
      type: 1,
      is_active: false,
      question: {
        company_name: this.npsQuestionStore.companyName,
      },
      filters: this.enrollContactsStore.paramsFilters,
      send_from: this.enrollContactsStore.numberStore.paramsSendFrom,
      settings: {
        allow_re_entering: {
          value: this.enrollContactsStore.enrollmentStore.isContactReEnroll,
        },
        ...this.fallbackActionsStore.params,
      },
    }
  }

  setError = (key: string, error: string) => {
    this.errorsMap.set(key, error)
  }

  removeError = (key: string) => {
    this.errorsMap.delete(key)
  }

  onSaveSurvey = async (props?: { withError?: true }) => {
    const withError = props?.withError

    if (!this.validateName()) {
      if (withError) throw new Error('Validation failed')
      return
    }
    this.isSaving = true

    try {
      if (this.id) {
        await SurveysApi.updateSurvey(this.id, this.params)
        this.blockerStore.updateCache()
      } else {
        const { data } = await SurveysApi.createSurvey(this.params)
        this.blockerStore.updateCache()
        uiStore.changeRoute({ path: SurveysPath.edit(data.id) })
      }

      toastStore.add({
        title: 'Survey saved',
        type: 'info',
      })
    } catch (error) {
      if (error instanceof AxiosError) this._syncErrors(error.response?.data?.errors ?? {})
      logger.error(error)

      if (withError) throw error
      return null
    } finally {
      this.isSaving = false
    }
  }

  onActivateSurvey = async (props?: { withError?: true; skipNavigation?: boolean }) => {
    if (!this.checkValidate()) {
      if (props?.withError) throw new Error('Validation failed')
      return
    }
    this.isSaving = true

    const payload = { ...this.params, is_active: true }
    try {
      if (this.id) {
        await SurveysApi.updateSurvey(this.id, payload)
      } else {
        await SurveysApi.createSurvey(payload)
      }
      toastStore.add({
        title: this.isActive ? 'Survey published' : 'Survey activated',
        type: 'success',
      })

      this.blockerStore.updateCache()
      if (!props?.skipNavigation) {
        uiStore.changeRoute({ path: `${SurveysPath.root}` })
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        this._syncErrors(error.response?.data?.errors ?? {})

        if (error.response?.status === 500) {
          toastStore.add({
            title: 'Something went wrong',
            desc: 'Please try again',
            type: 'error',
          })
        }
      }
      logger.error(error)
      if (props?.withError) throw error
    } finally {
      this.isSaving = false
    }
  }

  private _syncErrors = (errors: Record<string, string[]>) => {
    const sendFromError = errors['send_from']?.[0] ?? errors['send_from.options']?.[0]

    if (sendFromError) this.enrollContactsStore.numberStore.setError(sendFromError)
    if (errors['name']) {
      this.nameInputStore.setError(errors['name'][0])
    }
    if (errors['question.company_name']) {
      this.npsQuestionStore.setError(errors['question.company_name'][0])
    }
  }

  validateName = () => {
    if (!this.nameInputStore.name) {
      this.nameInputStore.setError('Please enter survey name')
      return false
    }
    return true
  }

  checkValidate = () => {
    let isValid = true

    if (!this.validateName()) {
      isValid = false
    }

    if (!this.npsQuestionStore.companyName) {
      this.npsQuestionStore.setError('Please enter company or product name')
      isValid = false
    }

    return isValid
  }

  disableSurvey = () => {
    if (!this.id) return

    openDisableSurveyModal(this.id, (data) => {
      this.isActive = data.is_active
      this.status = data.status
      this._syncBlockerConfig()
    })
  }

  dispose = () => {
    this.enrollContactsStore.dispose()
  }
}
